import { Controller, FieldValues, UseFormReturn } from "react-hook-form";
import FloatingInput from "./date-time-input/FloatingInput";
import {
  FieldTypes,
  IField,
} from "@/api/AppApi/services/builder/interfaces/interfaces";
import { validateFormController } from "../../form-builder-helpers/form-functions";
import { useTranslation } from "@/hooks/localization/useTranslations";

interface Props {
  field: IField;
  form: UseFormReturn<FieldValues, any>;
  minDate?: Date;
  className?: string;
  id?: string;
}

// Helper to format Date to input string value
const formatDateValue = (date: Date | null | string, type: FieldTypes): string => {
  if (!date) return "";
  const d = typeof date === "string" ? new Date(date) : date;
  if (isNaN(d.getTime())) return "";

  const pad = (n: number) => n.toString().padStart(2, "0");
  const yyyy = d.getFullYear();
  const MM = pad(d.getMonth() + 1);
  const dd = pad(d.getDate());
  const hh = pad(d.getHours());
  const mm = pad(d.getMinutes());

  if (type === FieldTypes.date) return `${yyyy}-${MM}-${dd}`;
  if (type === FieldTypes.time) return `${hh}:${mm}`;
  if (type === FieldTypes.date_time) return `${yyyy}-${MM}-${dd}T${hh}:${mm}`;
  return "";
};

// Helper to parse input string to Date
const parseDateValue = (value: string, type: FieldTypes): Date | null => {
  if (!value) return null;

  if (type === FieldTypes.time) {
    const [h, m] = value.split(":");
    const now = new Date();
    now.setHours(Number(h));
    now.setMinutes(Number(m));
    now.setSeconds(0);
    return now;
  }

  return new Date(value);
};

const CommonDateTimeInput: React.FC<Props> = ({ form, field, className, id }) => {
  const t = useTranslation();
  const fConfig = field.configurations;

  return (
    <div className={className} id={id}>
      <Controller
        control={form.control}
        name={`${fConfig.key}.value`}
        rules={{
          validate: (value) =>
            validateFormController({
              form,
              fieldName: fConfig.key,
              value,
              is_required: fConfig?.is_required,
              requiredMsg: t.this_field_is_required,
            }),
        }}
        render={({ field: controllerField }) => (
          <FloatingInput
            label={fConfig.title}
            type={fConfig.type}
            isRequired={fConfig.is_required}
            errorMsg={form.formState.errors[fConfig.key]?.message as string}
            hideLabel={fConfig.hideLabel}
            hint={fConfig.hint}
            value={formatDateValue(controllerField.value, fConfig.type)}
            onChange={(e: any) => {
              const val = e.target.value;
              const dateObj = parseDateValue(val, fConfig.type);
              controllerField.onChange(dateObj);
            }}
            ref={controllerField.ref}
          />
        )}
      />
    </div>
  );
};

export default CommonDateTimeInput;
